/**
 * Header behaviour: glass bar after 80px, hide on scroll down / show on scroll up,
 * the rose scroll-progress bar, the footer back-to-top ring, and the mobile overlay menu.
 */
import { stopScroll, startScroll, scrollToTarget, getLenis } from './lenis.js';

export function initHeader() {
  const header = document.querySelector('[data-header]');
  const progress = document.querySelector('.scroll-progress');
  const ring = document.querySelector('[data-ring]');
  const bottomBar = document.querySelector('[data-bottom-bar]');
  let lastY = window.scrollY;
  let ticking = false;

  const update = () => {
    ticking = false;
    const y = window.scrollY;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const p = max > 0 ? Math.min(1, Math.max(0, y / max)) : 0;

    header.classList.toggle('is-scrolled', y > 80);
    const menuOpen = document.body.classList.contains('menu-open');
    const focusInHeader = header.contains(document.activeElement);
    if (!menuOpen && !focusInHeader && y > 240 && y > lastY + 4) header.classList.add('is-hidden');
    else if (y < lastY - 4 || y <= 240) header.classList.remove('is-hidden');
    lastY = y;

    if (progress) progress.style.transform = `scaleX(${p})`;
    if (ring) ring.style.strokeDashoffset = String(1 - p);
  };

  const onScroll = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  header.addEventListener('focusin', () => header.classList.remove('is-hidden'));
  update();

  // Back to top
  document.querySelector('[data-to-top]')?.addEventListener('click', () => {
    const lenis = getLenis();
    if (lenis) lenis.scrollTo(0, { duration: 1.6 });
    else window.scrollTo({ top: 0, behavior: document.documentElement.classList.contains('reduced-motion') ? 'auto' : 'smooth' });
    document.getElementById('main')?.focus({ preventScroll: true });
  });

  // The mobile bottom bar steps aside while the sign-up form is on screen, so it never covers it.
  const form = document.getElementById('signup');
  if (bottomBar && form && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => bottomBar.classList.toggle('is-hidden', entry.isIntersecting), { threshold: 0.15 }).observe(form);
  }

  initMenu();
}

function initMenu() {
  const toggle = document.querySelector('[data-menu-toggle]');
  const menu = document.querySelector('[data-mobile-menu]');
  const label = document.querySelector('[data-menu-label]');
  if (!toggle || !menu) return;
  let closeTimer = null;

  const focusables = () => [toggle, ...menu.querySelectorAll('a[href], button:not([disabled])')];

  const open = () => {
    clearTimeout(closeTimer);
    menu.hidden = false;
    void menu.offsetWidth; // let the hidden->visible change apply before the transition starts
    menu.classList.add('is-open');
    document.body.classList.add('menu-open');
    toggle.setAttribute('aria-expanded', 'true');
    label.textContent = 'Close menu';
    stopScroll();
    setTimeout(() => menu.querySelector('a')?.focus(), 350);
  };

  const close = (returnFocus = true) => {
    menu.classList.remove('is-open');
    document.body.classList.remove('menu-open');
    toggle.setAttribute('aria-expanded', 'false');
    label.textContent = 'Open menu';
    startScroll();
    closeTimer = setTimeout(() => { menu.hidden = true; }, 750);
    if (returnFocus) toggle.focus();
  };

  toggle.addEventListener('click', () => (toggle.getAttribute('aria-expanded') === 'true' ? close() : open()));

  document.addEventListener('keydown', (e) => {
    if (!menu.classList.contains('is-open')) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'Tab') {
      // Keep focus inside the toggle + menu while it is open.
      const items = focusables();
      const i = items.indexOf(document.activeElement);
      if (e.shiftKey && (i <= 0)) { e.preventDefault(); items[items.length - 1].focus(); }
      else if (!e.shiftKey && i === items.length - 1) { e.preventDefault(); items[0].focus(); }
    }
  });

  menu.addEventListener('click', (e) => {
    const link = e.target.closest('a');
    if (!link) return;
    const url = new URL(link.href, location.href);
    close(false);
    if (url.pathname === location.pathname && url.hash) {
      e.preventDefault();
      setTimeout(() => scrollToTarget(document.getElementById(url.hash.slice(1))), 100);
    }
  });

  // Close if the viewport grows to desktop while open.
  window.matchMedia('(min-width: 900px)').addEventListener('change', (mq) => {
    if (mq.matches && menu.classList.contains('is-open')) close(false);
  });
}
